"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { apiFetch } from "@/lib/api/client";

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  created_at: string | null;
  is_email_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (user: User) => void;
  updateUser: (user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await apiFetch("/users/me/");
        if (response && !response.error) {
          setUser(response);
        }
      } catch (error) {
        // Will fail if not logged in, which is expected
      } finally {
        setIsLoading(false);
      }
    }
    fetchUser();
  }, []);

  // Route Protection Effect
  useEffect(() => {
    if (isLoading) return;

    const isAuthRoute = pathname === "/login" || pathname === "/register";
    const isDashboardRoute = pathname.startsWith("/dashboard");

    if (user && isAuthRoute) {
      // If logged in and trying to access login/register, redirect to dashboard
      router.push("/dashboard");
    } else if (!user && isDashboardRoute) {
      // If NOT logged in and trying to access dashboard, redirect to login
      router.push("/login");
    }
  }, [user, isLoading, pathname, router]);

  const login = (userData: User) => {
    setUser(userData);
    router.push("/dashboard");
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };
  
  const logout = async () => {
    try {
      await apiFetch("/users/logout/", { method: "POST" });
      setUser(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Failed to logout", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, updateUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
